import { Box } from "@mui/material";
import { handleHtmlTags } from "@platformx/utilities";
import { lazy, memo, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { Controller } from "react-hook-form";
import { fetchValidations, findMaxCharLength } from "../../../utils/helperFns";
import ErrorMessageHandle from "../Error/ErrorMessageHandle";
import MaxLengthValidationMessage from "../Error/MaxLengthValidationMessage";

const LazyBasicLexicalEditor = lazy(() =>
  import("@platformx/lexical-editor").then((module) => ({ default: module.BasicLexicalEditor })),
);

const getVisibleCharacterCount = (input: string) => input.replace(/[\r\n\u00A0\u200B]/g, "").length;

interface EditorProps {
  field: {
    name: string;
    validations?: any;
  };
  errors: Record<string, any>;
  control: any;
  data: any;
  updateState: any;
  title?: string;
}

const Editor = memo(({ field, errors, control, data, updateState, title }: EditorProps) => {
  const [value, setValue] = useState("");

  useEffect(() => {
    setValue(data || "");
  }, [data]);

  const validations = useMemo(
    () => fetchValidations(field?.validations || {}),
    [field?.validations],
  );
  const maxCharLength = findMaxCharLength(validations);
  const requiredError = useCallback(() => {
    return field.validations ? field.validations.find((err) => err.type === "required") : null;
  }, [field.validations]);

  const onChangeEditor = useCallback(
    (htmlData) => {
      setValue(htmlData); // Set the HTML content to state
      updateState(field.name, htmlData); // Update the state with the HTML content
    },
    [field.name, updateState],
  );

  const restOfChar = useMemo(() => {
    const textLength = getVisibleCharacterCount(handleHtmlTags(value || data || "") || "");
    return {
      lengthOfState: textLength,
      restOfLength: validations?.maxLength?.value - textLength,
      reachLimit: textLength >= validations?.maxLength?.value,
    };
  }, [data, value, validations?.maxLength?.value]);

  return (
    <Box id={`${field.name}_id`}>
      <Controller
        name={field.name}
        control={control}
        rules={{
          validate: () => {
            const error = requiredError();
            const htmlData = getVisibleCharacterCount(handleHtmlTags(value || data || "") || "");
            return error ? htmlData > 0 || requiredError()?.message : true;
          },
        }}
        render={({ fieldState: { error } }) => (
          <>
            <Suspense fallback={null}>
              <LazyBasicLexicalEditor
                data={data || ""}
                onChange={onChangeEditor}
                isPrelem={true}
                autoFocus={false}
                title={title}
                descMaxLength={maxCharLength}
              />
            </Suspense>
            <ErrorMessageHandle error={error} />
            <MaxLengthValidationMessage
              validations={field?.validations}
              error={errors?.[field?.name]}
              restOfChar={restOfChar}
            />
          </>
        )}
      />
    </Box>
  );
});

export default Editor;
Editor.displayName = "Editor";
